import { isNotDefined } from '@sgme/fp';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { FC } from 'react';
import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import { amountDefaultAccelerators } from 'utils/amount';

import { NumberFormatter } from '../NumberFormatter/NumberFormatter';
import type { StyledNumberFormatterProps } from '../NumberFormatter/NumberFormatter.model';
import type { WithFormLabelProps } from '../WithLabel/WithLabel';
import { WithFormLabel } from '../WithLabel/WithLabel';
import type { WithUnitProps } from '../WithUnit/WithUnit';
import { WithUnit, WithUnitSkeleton } from '../WithUnit/WithUnit';

export type AmountProps = StyledNumberFormatterProps;

export const Amount: FC<AmountProps> = props => {
  const onBlur = (value?: number) => {
    if (props.onBlur) {
      props.onBlur(value);
    }
  };

  return (
    <NumberFormatter
      {...props}
      onBlur={onBlur}
      accelerators={props.accelerators || amountDefaultAccelerators}
      incrementStep={props.incrementStep !== undefined ? props.incrementStep : 1000000}
      tabIndex={props.tabIndex}
    />
  );
};

export const AmountSkeleton: FC = () => (
  <div className="form-control">
    <Skeleton width="4em" />
  </div>
);

export type AmountWithUnitProps = AmountProps & WithUnitProps & { children?: ReactNode };
export const AmountWithUnit = (props: AmountWithUnitProps) => {
  const intl = useIntl();

  const { unit, units, onUnitSelected, unitsDirection, tabIndex, highlightColor, ...amountProps } = props;

  return (
    <WithUnit
      unit={getUnit(intl, unit)}
      units={units}
      onUnitSelected={onUnitSelected}
      unitsDirection={unitsDirection}
      appendedChildren={props.appendedChildren}
      e2e={amountProps.e2e}
      unitE2e={amountProps.unitE2e}
      className={classNames({
        'is-highlighted-success': highlightColor === 'success',
        'is-highlighted-info': highlightColor === 'info',
        'is-highlighted-turquoise': highlightColor === 'turquoise',
      })}
    >
      {props.children}
      <Amount tabIndex={tabIndex} highlightColor={highlightColor} {...amountProps} />
    </WithUnit>
  );
};

export const AmountWithUnitSkeleton: FC<{ unit?: string }> = ({ unit }) => {
  return (
    <WithUnitSkeleton unit={unit}>
      <AmountSkeleton />
    </WithUnitSkeleton>
  );
};

export const AmountWithUnitAndLabel = (props: AmountProps & WithUnitProps & WithFormLabelProps) => {
  const { id, colSize, label, labelKey, useFlex, className, tabIndex, ...amountProps } = props;
  return (
    <WithFormLabel id={id} label={label} useFlex={useFlex} className={className} labelKey={labelKey} colSize={colSize}>
      <AmountWithUnit id={id} tabIndex={tabIndex} {...amountProps} />
    </WithFormLabel>
  );
};

function getUnit(intl: IntlShape, unit?: string): string | undefined {
  if (isNotDefined(unit)) {
    return undefined;
  }
  switch (unit) {
    case 'Business days':
      return intl.formatMessage({ id: 'form.unit.BusinessDays', defaultMessage: 'BDs' });
    default:
      return unit;
  }
}
